const DEVICE_TRANSFER_CURVE = 'P-256'
const DEVICE_TRANSFER_VERSION = 1
const SAS_INFO = 'treechat-device-transfer:sas:v1'
const PAYLOAD_INFO = 'treechat-device-transfer:payload:v1'
const CONFIRM_INFO = 'treechat-device-transfer:confirm:v1'
const AES_GCM_NONCE_BYTES = 12
const textEncoder = new TextEncoder()
const textDecoder = new TextDecoder()

export interface DeviceTransferTranscript {
    sessionId: string
    requestingDeviceId: string
    approvingDeviceId: string
    requesterEphemeralPublicKey: string
    approverEphemeralPublicKey: string
}

export type DeviceTransferRole = 'requester' | 'approver'

export interface DeviceTransferKeyPair {
    privateKey: CryptoKey
    publicKey: CryptoKey
    publicKeyBase64Url: string
}

export interface DeviceTransferEnvelope {
    encryptedWalletPayload: string
    encryptedWalletNonce: string
}

function requireWebCrypto(): Crypto {
    if (typeof crypto === 'undefined' || !crypto.subtle) {
        throw new Error('Web Crypto is not available in this browser')
    }

    return crypto
}

function bytesToBase64Url(bytes: Uint8Array): string {
    let binary = ''
    for (const byte of bytes) {
        binary += String.fromCharCode(byte)
    }

    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/u, '')
}

function base64UrlToBytes(encoded: string): Uint8Array {
    const padding = '='.repeat((4 - (encoded.length % 4 || 4)) % 4)
    const base64 = encoded.replace(/-/g, '+').replace(/_/g, '/') + padding
    const binary = atob(base64)
    const bytes = new Uint8Array(binary.length)

    for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index)
    }

    return bytes
}

// The transcript ordering here must stay aligned with the transfer-session contract
// so both browsers derive the same SAS digits and payload key for the same session.
function serializeTranscript(transcript: DeviceTransferTranscript): Uint8Array {
    const normalizedTranscript = {
        version: DEVICE_TRANSFER_VERSION,
        sessionId: transcript.sessionId,
        requestingDeviceId: transcript.requestingDeviceId,
        approvingDeviceId: transcript.approvingDeviceId,
        requesterEphemeralPublicKey: transcript.requesterEphemeralPublicKey,
        approverEphemeralPublicKey: transcript.approverEphemeralPublicKey,
    }

    return textEncoder.encode(JSON.stringify(normalizedTranscript))
}

async function importHkdfKey(sharedSecret: BufferSource): Promise<CryptoKey> {
    const webCrypto = requireWebCrypto()

    return webCrypto.subtle.importKey('raw', sharedSecret, 'HKDF', false, [
        'deriveBits',
        'deriveKey',
    ])
}

async function deriveHkdfBits(
    sharedSecret: BufferSource,
    transcript: DeviceTransferTranscript,
    info: string,
    bitLength: number,
): Promise<ArrayBuffer> {
    const webCrypto = requireWebCrypto()
    const transcriptBytes = serializeTranscript(transcript)
    const hkdfKey = await importHkdfKey(sharedSecret)

    return webCrypto.subtle.deriveBits(
        {
            name: 'HKDF',
            hash: 'SHA-256',
            salt: transcriptBytes,
            info: textEncoder.encode(info),
        },
        hkdfKey,
        bitLength,
    )
}

export async function generateDeviceTransferKeyPair(): Promise<DeviceTransferKeyPair> {
    const webCrypto = requireWebCrypto()
    const keyPair = await webCrypto.subtle.generateKey(
        {
            name: 'ECDH',
            namedCurve: DEVICE_TRANSFER_CURVE,
        },
        true,
        ['deriveBits'],
    )

    const publicKey = keyPair.publicKey as CryptoKey
    const privateKey = keyPair.privateKey as CryptoKey
    const publicKeyBase64Url = await exportDeviceTransferPublicKey(publicKey)

    return {
        privateKey,
        publicKey,
        publicKeyBase64Url,
    }
}

export async function exportDeviceTransferPublicKey(publicKey: CryptoKey): Promise<string> {
    const webCrypto = requireWebCrypto()
    const publicKeyBytes = await webCrypto.subtle.exportKey('raw', publicKey)
    return bytesToBase64Url(new Uint8Array(publicKeyBytes))
}

export async function importDeviceTransferPublicKey(
    publicKeyBase64Url: string,
): Promise<CryptoKey> {
    const webCrypto = requireWebCrypto()
    const publicKeyBytes = base64UrlToBytes(publicKeyBase64Url)

    return webCrypto.subtle.importKey(
        'raw',
        publicKeyBytes,
        {
            name: 'ECDH',
            namedCurve: DEVICE_TRANSFER_CURVE,
        },
        true,
        [],
    )
}

export async function deriveDeviceTransferSharedSecret(
    privateKey: CryptoKey,
    remotePublicKeyBase64Url: string,
): Promise<Uint8Array> {
    const webCrypto = requireWebCrypto()
    const remotePublicKey = await importDeviceTransferPublicKey(remotePublicKeyBase64Url)
    const sharedSecret = await webCrypto.subtle.deriveBits(
        {
            name: 'ECDH',
            public: remotePublicKey,
        },
        privateKey,
        256,
    )

    return new Uint8Array(sharedSecret)
}

export async function deriveDeviceTransferSasBytes(
    sharedSecret: BufferSource,
    transcript: DeviceTransferTranscript,
): Promise<Uint8Array> {
    const sasBits = await deriveHkdfBits(sharedSecret, transcript, SAS_INFO, 40)
    return new Uint8Array(sasBits)
}

export function decimalSasNumbersFromBytes(sasBytes: Uint8Array): [number, number, number] {
    if (sasBytes.length < 5) {
        throw new Error('At least 5 bytes are required to derive decimal SAS numbers')
    }

    const first = ((sasBytes[0] << 5) | (sasBytes[1] >> 3)) + 1000
    const second = (((sasBytes[1] & 0x07) << 10) | (sasBytes[2] << 2) | (sasBytes[3] >> 6)) + 1000
    const third = (((sasBytes[3] & 0x3f) << 7) | (sasBytes[4] >> 1)) + 1000

    return [first, second, third]
}

export async function deriveDeviceTransferSasNumbers(
    sharedSecret: BufferSource,
    transcript: DeviceTransferTranscript,
): Promise<[number, number, number]> {
    const sasBytes = await deriveDeviceTransferSasBytes(sharedSecret, transcript)
    return decimalSasNumbersFromBytes(sasBytes)
}

export async function deriveDeviceTransferPayloadKey(
    sharedSecret: BufferSource,
    transcript: DeviceTransferTranscript,
): Promise<CryptoKey> {
    const webCrypto = requireWebCrypto()
    const transcriptBytes = serializeTranscript(transcript)
    const hkdfKey = await importHkdfKey(sharedSecret)

    return webCrypto.subtle.deriveKey(
        {
            name: 'HKDF',
            hash: 'SHA-256',
            salt: transcriptBytes,
            info: textEncoder.encode(PAYLOAD_INFO),
        },
        hkdfKey,
        {
            name: 'AES-GCM',
            length: 256,
        },
        false,
        ['encrypt', 'decrypt'],
    )
}

export async function deriveDeviceTransferConfirmKey(
    sharedSecret: BufferSource,
    transcript: DeviceTransferTranscript,
): Promise<CryptoKey> {
    const webCrypto = requireWebCrypto()
    const bits = await deriveHkdfBits(sharedSecret, transcript, CONFIRM_INFO, 256)

    return webCrypto.subtle.importKey('raw', bits, { name: 'HMAC', hash: 'SHA-256' }, false, [
        'sign',
        'verify',
    ])
}

function sasProofMessage(
    role: DeviceTransferRole,
    sasNumbers: readonly number[],
    transcript: DeviceTransferTranscript,
): Uint8Array {
    return textEncoder.encode(
        JSON.stringify({
            role,
            sas: sasNumbers,
            transcript: JSON.parse(textDecoder.decode(serializeTranscript(transcript))),
        }),
    )
}

// HMAC over (role, SAS numbers, full transcript) keyed from the ECDH shared secret. Only a
// holder of one of the two ephemeral private keys can produce it, so the server cannot forge
// a peer's confirmation by flipping its own "confirmed" flag.
export async function computeDeviceTransferSasProof(
    confirmKey: CryptoKey,
    role: DeviceTransferRole,
    sasNumbers: readonly number[],
    transcript: DeviceTransferTranscript,
): Promise<string> {
    const webCrypto = requireWebCrypto()
    const signature = await webCrypto.subtle.sign(
        'HMAC',
        confirmKey,
        sasProofMessage(role, sasNumbers, transcript),
    )

    return bytesToBase64Url(new Uint8Array(signature))
}

export async function verifyDeviceTransferSasProof(
    confirmKey: CryptoKey,
    role: DeviceTransferRole,
    sasNumbers: readonly number[],
    transcript: DeviceTransferTranscript,
    proof: string | null | undefined,
): Promise<boolean> {
    if (!proof) {
        return false
    }

    try {
        const webCrypto = requireWebCrypto()
        return await webCrypto.subtle.verify(
            'HMAC',
            confirmKey,
            base64UrlToBytes(proof),
            sasProofMessage(role, sasNumbers, transcript),
        )
    } catch (_error) {
        return false
    }
}

export async function deriveDeviceTransferSessionMaterial(
    privateKey: CryptoKey,
    remotePublicKeyBase64Url: string,
    transcript: DeviceTransferTranscript,
): Promise<{
    sharedSecret: Uint8Array
    sasNumbers: [number, number, number]
    payloadKey: CryptoKey
    confirmKey: CryptoKey
}> {
    const sharedSecret = await deriveDeviceTransferSharedSecret(
        privateKey,
        remotePublicKeyBase64Url,
    )
    const [sasNumbers, payloadKey, confirmKey] = await Promise.all([
        deriveDeviceTransferSasNumbers(sharedSecret, transcript),
        deriveDeviceTransferPayloadKey(sharedSecret, transcript),
        deriveDeviceTransferConfirmKey(sharedSecret, transcript),
    ])

    return {
        sharedSecret,
        sasNumbers,
        payloadKey,
        confirmKey,
    }
}

export async function encryptWalletPayload(
    walletJson: string,
    payloadKey: CryptoKey,
): Promise<DeviceTransferEnvelope> {
    const webCrypto = requireWebCrypto()
    const nonce = webCrypto.getRandomValues(new Uint8Array(AES_GCM_NONCE_BYTES))
    const walletBytes = textEncoder.encode(walletJson)
    const encryptedWalletPayload = await webCrypto.subtle.encrypt(
        {
            name: 'AES-GCM',
            iv: nonce,
        },
        payloadKey,
        walletBytes,
    )

    return {
        encryptedWalletPayload: bytesToBase64Url(new Uint8Array(encryptedWalletPayload)),
        encryptedWalletNonce: bytesToBase64Url(nonce),
    }
}

export async function decryptWalletPayload(
    encryptedWalletPayload: string,
    encryptedWalletNonce: string,
    payloadKey: CryptoKey,
): Promise<string> {
    const webCrypto = requireWebCrypto()
    const nonce = base64UrlToBytes(encryptedWalletNonce)
    const encryptedBytes = base64UrlToBytes(encryptedWalletPayload)
    const decryptedWalletBytes = await webCrypto.subtle.decrypt(
        {
            name: 'AES-GCM',
            iv: nonce,
        },
        payloadKey,
        encryptedBytes,
    )

    return textDecoder.decode(decryptedWalletBytes)
}
